import {Tooltip, Typography} from '@mui/material';
import {IReportsRow, ReportData, ReportTypes} from './model';
import {t} from 'i18next';
import {numberFormatter} from '../../../../utils/utils';

const tooltipClasses = {
  arrow: 'aur-leftnav_tooltip-arrow',
  tooltip: 'aur-leftnav_tooltip-tooltip',
};

function getColumnData(
  row: IReportsRow | undefined,
  key: keyof IReportsRow,
  showSuppressed: boolean
) {
  let value;
  if (row) {
    value = row[key];

    if (key === 'total') {
      value = numberFormatter(row[key] ?? 0);
    }
  }
  return (
    <Tooltip
      title={row && row[key]}
      placement="top"
      arrow
      disableInteractive
      classes={tooltipClasses}
      slotProps={{
        popper: {
          modifiers: [{name: 'offset', options: {offset: [8, -6]}}],
        },
      }}
    >
      <Typography
        className={`${showSuppressed && row && row.batchNumberSuppressed === 'Suppressed' ? ' suppressed' : ''} typo-text body3_size`}
      >
        {value ?? ''}
      </Typography>
    </Tooltip>
  );
}

export const getColumnByType = (type: string) => {
  let columns: {
    name: any;
    text: string;
  }[] = [
    {
      name: 'evidenceSource',
      text: t('reportHeaders.evidenceSource'),
    },
    {
      name: 'batchNumberSuppressed',
      text: t('reportHeaders.reviewExport'),
    },
  ];
  let showSuppressed = true;
  let width = 300;
  switch (type) {
    case ReportTypes.CORRUPTED_ACTIONABLE: {
      columns.push({
        name: 'total',
        text: t('reportHeaders.exceptions'),
      });
      break;
    }

    case ReportTypes.ENCRYPTED: {
      showSuppressed = false;
      columns = [
        {
          name: 'evidenceSource',
          text: t('reportHeaders.evidenceSource'),
        },
        {
          name: 'documentID',
          text: t('reportHeaders.documentID'),
        },
        {
          name: 'filename',
          text: t('reportHeaders.filename'),
        },
        {
          name: 'path',
          text: t('reportHeaders.path'),
        },
      ];
      width = 213;
      break;
    }
    case ReportTypes.NON_SEARCHABLE_PDFS: {
      columns.push({
        name: 'total',
        text: t('reportHeaders.nonSearchablePDFs'),
      });
      break;
    }
    case ReportTypes.CORRECTED_BAD_EXTENSIONS: {
      columns.push({
        name: 'total',
        text: t('reportHeaders.correctedBadExtension'),
      });
      break;
    }
    case ReportTypes.RECOVERED_DELETED_EMAILS: {
      columns.push({
        name: 'total',
        text: t('reportHeaders.recoveredDeletedEmails'),
      });
      break;
    }
    case ReportTypes.CORRUPTED_NON_ACTIONABLE: {
      columns.push({
        name: 'total',
        text: t('reportHeaders.corruptedNonActionable'),
      });
      break;
    }
    case ReportTypes.EMPTY_ZERO_BYTE_FILES: {
      columns.push({
        name: 'total',
        text: t('reportHeaders.emptyZeroByteFiles'),
      });
      break;
    }
    case ReportTypes.PROBLEMATIC_TEXT: {
      columns.push({
        name: 'total',
        text: t('reportHeaders.problematicText'),
      });
      break;
    }
    case ReportTypes.UNRECOGNIZED_FILE: {
      columns.push({
        name: 'total',
        text: t('reportHeaders.unrecognizedFile'),
      });
      break;
    }
    case ReportTypes.UNSUPPORTED_FILE: {
      columns.push({
        name: 'total',
        text: t('reportHeaders.unsupportedFile'),
      });
      break;
    }
  }

  return columns.map((col) => {
    return {
      field: col.name,
      headerName: col.text,
      headerClassName: 'typo-text label2_size',
      width: width,
      sortable: false,
      renderCell: (params: any) => {
        const row = params.row as IReportsRow;
        params.value = getColumnData(row, col.name, showSuppressed);
        return params.value;
      },
      isBorderNotRequired: true,
    };
  });
};

export const getReportDataByType = (type: string) => {
  const data: ReportData = {
    title: '',
    description: '',
    userName: '',
    type: '',
  };
  switch (type) {
    case ReportTypes.CORRUPTED_ACTIONABLE: {
      data.title = t('reportData.title.corruptedActionable');
      data.description = t('reportData.description.corruptedActionable');
      data.userName = t('reportData.userName');
      data.total = 219;
      data.type = 'CorruptedActionable';
      break;
    }

    case ReportTypes.ENCRYPTED: {
      data.title = t('reportData.title.encrypted');
      data.description = t('reportData.description.encrypted');
      data.userName = t('reportData.userName');
      data.type = 'Encrypted';
      data.total = 1;
      break;
    }

    case ReportTypes.NON_SEARCHABLE_PDFS: {
      data.title = t('reportData.title.nonSearchablePDFs');
      data.description = t('reportData.description.nonSearchablePDFs');
      data.userName = t('reportData.userName');
      data.total = 11;
      data.type = 'NonSearchablePDFs';
      break;
    }
    case ReportTypes.CORRECTED_BAD_EXTENSIONS: {
      data.title = t('reportData.title.correctedBadExtensions');
      data.description = t('reportData.description.correctedBadExtensions');
      data.userName = t('reportData.userName');
      data.total = 150;
      data.type = 'BadExtension';
      break;
    }

    case ReportTypes.RECOVERED_DELETED_EMAILS: {
      data.title = t('reportData.title.recoveredDeletedEmails');
      data.description = t('reportData.description.recoveredDeletedEmails');
      data.userName = t('reportData.userName');
      data.total = 11;
      data.type = 'Deleted';
      break;
    }
    case ReportTypes.CORRUPTED_NON_ACTIONABLE: {
      data.title = t('reportData.title.corruptedNonActionable');
      data.description = t('reportData.description.corruptedNonActionable');
      data.userName = t('reportData.userName');
      data.total = 11;
      data.type = 'CorruptedNonActionable';
      break;
    }
    case ReportTypes.EMPTY_ZERO_BYTE_FILES: {
      data.title = t('reportData.title.emptyZeroByteFiles');
      data.description = t('reportData.description.emptyZeroByteFiles');
      data.userName = t('reportData.userName');
      data.total = 11;
      data.type = 'Empty';
      break;
    }
    case ReportTypes.PROBLEMATIC_TEXT: {
      data.title = t('reportData.title.problematicText');
      data.description = t('reportData.description.problematicText');
      data.userName = t('reportData.userName');
      data.total = 11;
      data.type = 'TextStripped';
      break;
    }
    case ReportTypes.UNRECOGNIZED_FILE: {
      data.title = t('reportData.title.unrecognizedFile');
      data.description = t('reportData.description.unrecognizedFile');
      data.userName = t('reportData.userName');
      data.total = 11;
      data.type = 'Unrecognized';
      break;
    }
    case ReportTypes.UNSUPPORTED_FILE: {
      data.title = t('reportData.title.unsupportedFile');
      data.description = t('reportData.description.unsupportedFile');
      data.userName = t('reportData.userName');
      data.total = 11;
      data.type = 'UnsupportedItems';
      break;
    }
  }

  return data;
};
